// =============================================================================
// staticSite/cfPagesApi — Cloudflare Pages przez proxy /api/cloudflare-pages/
// (nginx bez wymuszonego Content-Type, 64 MB body, za auth_request).
//
// Dwa rodzaje autoryzacji:
//   - token konta CF (cloudflare_accounts.api_token, uprawnienie Cloudflare Pages:Edit)
//     -> projekty, deployments, domeny
//   - JWT z upload-token -> /pages/assets/* (check-missing, upload, upsert-hashes)
// Protokol Direct Upload = to, co robi `wrangler pages deploy`.
// =============================================================================

/**
 * Baza URL. Przegladarka: proxy nginx (auth_request, token w Authorization).
 * build-worker (Node): configureCfPagesBase("https://api.cloudflare.com/client/v4")
 * — wzgledny URL w Node rzuca "Failed to parse URL", a proxy i tak nie ma jak
 * dostac naglowka X-Supabase-Auth spoza przegladarki.
 */
let CF_PAGES_BASE = "/api/cloudflare-pages";

export function configureCfPagesBase(baseUrl: string): void {
  CF_PAGES_BASE = baseUrl.replace(/\/+$/, "");
}

export function getCfPagesBase(): string {
  return CF_PAGES_BASE;
}

export class CfPagesError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
  }
}

interface CfResponse<T> {
  success: boolean;
  result: T;
  errors?: Array<{ code: number; message: string }>;
  result_info?: { page: number; total_pages: number; total_count?: number };
}

async function cfPagesFetch<T>(token: string, path: string, init: RequestInit = {}, timeoutMs = 120_000): Promise<CfResponse<T>> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const isForm = typeof FormData !== "undefined" && init.body instanceof FormData;
    const res = await fetch(`${CF_PAGES_BASE}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(isForm ? {} : { "Content-Type": "application/json" }),
        ...(init.headers || {}),
      },
      signal: controller.signal,
    });
    let json: any = null;
    try {
      json = await res.json();
    } catch {
      throw new CfPagesError(`Cloudflare zwrócił odpowiedź nie-JSON (HTTP ${res.status}) — sprawdź, czy proxy /api/cloudflare-pages/ jest wdrożone.`, res.status);
    }
    if (!json?.success) {
      const msg = json?.errors?.map((e: any) => `${e.code}: ${e.message}`).join("; ") || `HTTP ${res.status}`;
      throw new CfPagesError(msg, res.status);
    }
    return json as CfResponse<T>;
  } catch (err) {
    if (err instanceof CfPagesError) throw err;
    if ((err as Error)?.name === "AbortError") throw new CfPagesError(`Cloudflare nie odpowiedział w ${Math.round(timeoutMs / 1000)} s.`);
    throw new CfPagesError(`Połączenie z Cloudflare nieudane: ${(err as Error)?.message || err}`);
  } finally {
    clearTimeout(timer);
  }
}

// -----------------------------------------------------------------------------
// Projekty
// -----------------------------------------------------------------------------

export interface CfPagesProject {
  id: string;
  name: string;
  subdomain: string; // <name>.pages.dev
  production_branch: string;
  domains?: string[];
  created_on?: string;
}

/**
 * GET /pages/projects NIE obsluguje page/per_page — kazda wartosc (nawet
 * per_page=25) daje 8000024 "Invalid list options provided". Zweryfikowane
 * bezposrednio na koncie 2026-09-22. Endpoint zwraca WSZYSTKIE projekty
 * jednym zapytaniem bez paginacji (limit kont to i tak 100 projektow).
 */
export async function listPagesProjects(token: string, accountId: string): Promise<CfPagesProject[]> {
  const res = await cfPagesFetch<CfPagesProject[]>(token, `/accounts/${accountId}/pages/projects`);
  return res.result || [];
}

export async function getPagesProject(token: string, accountId: string, name: string): Promise<CfPagesProject | null> {
  try {
    const res = await cfPagesFetch<CfPagesProject>(token, `/accounts/${accountId}/pages/projects/${name}`);
    return res.result;
  } catch (e) {
    if (e instanceof CfPagesError && (e.status === 404 || /not found|8000007/i.test(e.message))) return null;
    throw e;
  }
}

export async function createPagesProject(token: string, accountId: string, name: string, productionBranch = "main"): Promise<CfPagesProject> {
  const res = await cfPagesFetch<CfPagesProject>(token, `/accounts/${accountId}/pages/projects`, {
    method: "POST",
    body: JSON.stringify({ name, production_branch: productionBranch }),
  });
  return res.result;
}

// -----------------------------------------------------------------------------
// Direct Upload — assety
// -----------------------------------------------------------------------------

export async function getUploadToken(token: string, accountId: string, project: string): Promise<string> {
  const res = await cfPagesFetch<{ jwt: string }>(token, `/accounts/${accountId}/pages/projects/${project}/upload-token`);
  if (!res.result?.jwt) throw new CfPagesError("Brak JWT w odpowiedzi upload-token");
  return res.result.jwt;
}

/** Zwraca hashe, ktorych CF jeszcze NIE ma (trzeba wgrac). */
export async function checkMissingAssets(jwt: string, hashes: string[]): Promise<string[]> {
  if (!hashes.length) return [];
  const res = await cfPagesFetch<string[]>(jwt, `/pages/assets/check-missing`, {
    method: "POST",
    body: JSON.stringify({ hashes }),
  });
  return res.result || [];
}

export interface CfAssetUpload {
  key: string; // hash
  value: string; // base64
  metadata: { contentType: string };
  base64: true;
}

export async function uploadAssets(jwt: string, payload: CfAssetUpload[]): Promise<void> {
  if (!payload.length) return;
  await cfPagesFetch<unknown>(jwt, `/pages/assets/upload`, { method: "POST", body: JSON.stringify(payload) }, 180_000);
}

export async function upsertHashes(jwt: string, hashes: string[]): Promise<void> {
  if (!hashes.length) return;
  await cfPagesFetch<unknown>(jwt, `/pages/assets/upsert-hashes`, { method: "POST", body: JSON.stringify({ hashes }) });
}

// -----------------------------------------------------------------------------
// Deployment
// -----------------------------------------------------------------------------

export interface CfDeployment {
  id: string;
  url: string;
  environment?: string;
  latest_stage?: { name: string; status: string; ended_on?: string | null };
  stages?: Array<{ name: string; status: string }>;
  created_on?: string;
}

export async function createDeployment(
  token: string,
  accountId: string,
  project: string,
  args: { manifest: Record<string, string>; branch: string; redirects?: string; headers?: string },
): Promise<CfDeployment> {
  const form = new FormData();
  form.append("manifest", JSON.stringify(args.manifest));
  form.append("branch", args.branch);
  if (args.redirects) form.append("_redirects", new Blob([args.redirects], { type: "text/plain" }), "_redirects");
  if (args.headers) form.append("_headers", new Blob([args.headers], { type: "text/plain" }), "_headers");
  const res = await cfPagesFetch<CfDeployment>(token, `/accounts/${accountId}/pages/projects/${project}/deployments`, {
    method: "POST",
    body: form,
  }, 180_000);
  return res.result;
}

export async function getDeployment(token: string, accountId: string, project: string, deploymentId: string): Promise<CfDeployment> {
  const res = await cfPagesFetch<CfDeployment>(token, `/accounts/${accountId}/pages/projects/${project}/deployments/${deploymentId}`);
  return res.result;
}

// -----------------------------------------------------------------------------
// Custom domains (faza 3)
// -----------------------------------------------------------------------------

export interface CfPagesDomain {
  id?: string;
  name: string;
  status: string; // initializing | pending | active | ...
  verification_data?: { status?: string };
  validation_data?: { status?: string; method?: string };
}

export async function listCustomDomains(token: string, accountId: string, project: string): Promise<CfPagesDomain[]> {
  const res = await cfPagesFetch<CfPagesDomain[]>(token, `/accounts/${accountId}/pages/projects/${project}/domains`);
  return res.result || [];
}

export async function addCustomDomain(token: string, accountId: string, project: string, name: string): Promise<CfPagesDomain> {
  const res = await cfPagesFetch<CfPagesDomain>(token, `/accounts/${accountId}/pages/projects/${project}/domains`, {
    method: "POST",
    body: JSON.stringify({ name }),
  });
  return res.result;
}

export async function deleteCustomDomain(token: string, accountId: string, project: string, name: string): Promise<void> {
  await cfPagesFetch<unknown>(token, `/accounts/${accountId}/pages/projects/${project}/domains/${name}`, { method: "DELETE" });
}

// -----------------------------------------------------------------------------
// Usuwanie projektu (zakladka /cloudflare -> Pages; porzadki po testach)
// -----------------------------------------------------------------------------

/** DELETE /accounts/{id}/pages/projects/{name} — kasuje projekt razem z deploymentami i domenami. */
export async function deletePagesProject(token: string, accountId: string, name: string): Promise<void> {
  await cfPagesFetch<unknown>(token, `/accounts/${accountId}/pages/projects/${encodeURIComponent(name)}`, { method: "DELETE" });
}
